import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import AvilableServices from "../components/Services/AvilableServices";
import DateNavigation from "../components/TopSection/DateNavigation";
import FilterSection from "../components/Services/FilterSection";
import styles from "./TimesPage.module.css";
import { StoreService } from "../services/store-service";
import { getTimesheet } from "../services/server-actions";
import { ReactComponent as RedEllipse } from "../../src/assets/icons/RedEllipse.svg";
import { ReactComponent as SortIcon } from "../../src/assets/icons/Sort.svg";
import { DateTime } from "ts-luxon";
import NotFound from "../components/Services/NotFound";
import PersianDateDisplay from "../components/TopSection/PersianDateDisplay";

const TimesPage: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const categoryIdParam = searchParams.get("service");
  const partnerIdParam = searchParams.get("partnerId");
  const dateParam = searchParams.get("date");

  
  const parsedPartnerId =
    partnerIdParam && !isNaN(Number(partnerIdParam))
      ? Number(partnerIdParam)
      : null;

  const [filteredCategory, setFilteredCategory] = useState<number | null>(
    categoryIdParam ? parseInt(categoryIdParam) : null
  );
  const [filteredPartner, setFilteredPartner] = useState<number | null>(
    parsedPartnerId
  );

  
  const [selectedDate, setSelectedDate] = useState<DateTime>(() => {
    return dateParam ? DateTime.fromISO(dateParam) : DateTime.local();
  });

  const [selectedPersianDate, setSelectedPersianDate] = useState<string>();
  const [timesheet, setTimesheet] = useState<any[]>([]);
  const [timesheetLoading, setTimesheetLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    if (dateParam) {
      const parsedDate = DateTime.fromISO(dateParam);
      if (parsedDate.isValid) {
        setSelectedDate(parsedDate);
      }
    }
  }, [dateParam]);
  
  const categories = useMemo(() => {
    const storedCategories = StoreService.GetData("categories") || [];
    return storedCategories;
  }, []);

  const partners = useMemo(() => {
    const storedPartners = StoreService.GetData("partners") || [];
    return storedPartners.map((partner: any) => ({
      id: partner.id,
      name: `${partner.firstname} ${partner.lastname}`,
    }));
  }, []);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes("android")) {
      document.body.classList.add("android");
      document.body.classList.remove("ios");
    } else if (userAgent.includes("iphone") || userAgent.includes("ipad")) {
      document.body.classList.add("ios");
      document.body.classList.remove("android");
    } else {
      document.body.classList.remove("android");
      document.body.classList.remove("ios");
    }
  }, []);

  useEffect(() => {
    document.body.classList.add("times-page-safe-area-extend");
    return () => {
      document.body.classList.remove("times-page-safe-area-extend");
    };
  }, []);

  const fetchTimesheet = useCallback(
    async (
      date: DateTime,
      categoryId: number | null,
      partnerId: number | null
    ) => {
      try {
        setTimesheetLoading(true);
        setError(null);
        const timesheetData = await getTimesheet(
          date.toISODate() ?? "",

          categoryId ?? undefined,
          partnerId ?? undefined
        );
        setTimesheet(timesheetData);
      } catch (error) {
        console.error("❌ خطا در دریافت تایم‌شیت:", error);
        setError("خطا در دریافت تایم‌شیت. لطفاً دوباره تلاش کنید.");
      } finally {
        setTimesheetLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (categoryIdParam) {
      setFilteredCategory(parseInt(categoryIdParam));
    }
    if (parsedPartnerId !== null) {
      setFilteredPartner(parsedPartnerId);
    }
  }, [categoryIdParam, parsedPartnerId]);

  useEffect(() => {
    fetchTimesheet(selectedDate, filteredCategory, filteredPartner);
  }, [selectedDate, filteredCategory, filteredPartner, fetchTimesheet]);

  const handleFilterChange = useCallback(
    (categoryId: number | null, partnerId: number | null) => {
      setFilteredCategory(categoryId);
      setFilteredPartner(partnerId);

      categoryId !== null
        ? StoreService.SetData("categoryId", categoryId)
        : localStorage.removeItem("categoryId");

      partnerId !== null
        ? StoreService.SetData("partnerId", partnerId)
        : localStorage.removeItem("partnerId");
    },
    []
  );

  const handleDateChange = useCallback(
    (date: DateTime, persianDate: string) => {
      setSelectedDate(date);
      setSelectedPersianDate(persianDate);
      StoreService.SetData("selectedPersianDate", persianDate); // ⬅️ ذخیره در لوکال
    },
    []
  );

  const toggleFilter = useCallback(() => setIsFilterOpen((prev) => !prev), []);
  const hasReservations = useMemo(() => timesheet.length > 0, [timesheet]);
  const daysOfWeek = [
    "دوشنبه",
    "سه‌شنبه",
    "چهارشنبه",
    "پنج‌شنبه",
    "جمعه",
    "شنبه",
    "یک‌شنبه",
  ];
  const months = [
    "فروردین",
    "اردیبهشت",
    "خرداد",
    "تیر",
    "مرداد",
    "شهریور",
    "مهر",
    "آبان",
    "آذر",
    "دی",
    "بهمن",
    "اسفند",
  ];
  const dayIndex = selectedDate.weekday - 1;
  const dayName = daysOfWeek[dayIndex];
  const monthName = months[selectedDate.month - 1];
  const persianYear = selectedDate.year - 621;

  return (
    <div>
      <div className={styles.dateNavigation}>
      <DateNavigation onDateChange={handleDateChange} initialDate={selectedDate} />

      </div>

      <section className={styles.timesPageSection}>
        <div className="mb-2 d-flex justify-content-between align-items-center">
          <button
            onClick={toggleFilter}
            className={`${styles.filterButton} d-flex align-items-center justify-content-center`}
          >
            <span className={styles.filterButtonText}>
              فیلتر
              <SortIcon className={styles.sortIcon} />
              {filteredCategory !== null || filteredPartner !== null ? (
                <RedEllipse className={styles.redCircle} />
              ) : null}
            </span>
          </button>
          <h6 className={styles.timesTitle}>
            وقت‌های {dayName} {selectedPersianDate?.split("/")[2]}{" "}
            <PersianDateDisplay date={selectedDate} />
          </h6>
        </div>

        {isFilterOpen && (
          <div className="mb-2">
            <FilterSection
              onFilterChange={handleFilterChange}
              services={categories}
              partners={partners}
              selectedService={filteredCategory?.toString() ?? null}
              selectedPartner={filteredPartner?.toString() ?? null}
              selectedDateISO={selectedDate.toISODate()}
              hasReservations={hasReservations}
            />
          </div>
        )}

        <div className="d-flex justify-content-center">
          {timesheetLoading ? (
            <p className="text-center"></p>
          ) : error ? (
            <p className="text-center text-danger">{error}</p>
          ) : hasReservations ? (
            <AvilableServices
              reservations={timesheet}
              selectedCategoryId={filteredCategory}
              selectedPartnerId={filteredPartner}
              selectedPersianDate={selectedPersianDate}
              layout="vertical"
            />
          ) : (
            !isFilterOpen && <NotFound type="noReservations" />
          )}
        </div>
      </section>
    </div>
  );
};

export default TimesPage;
